"use client"

import type { Page } from "content-collections"
import { format } from "date-fns"
import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useLocale } from "next-intl"
import { useMemo, useState } from "react"
import { Button, buttonVariants } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { config } from "@/config"
import { cn } from "@/utils/cn"
import { Logo } from "../logo"

interface BlogTabsProps {
  allPosts: Page[]
}

export function BlogTabsAndPosts({ allPosts }: BlogTabsProps) {
  const [activeTab, setActiveTab] = useState("updates")
  const [isExpanded, setIsExpanded] = useState(false)

  const filteredPosts = useMemo(() => {
    switch (activeTab) {
      case "updates":
        return allPosts.filter((post) => post.collection === "blog" || post.collection === "tools")
      case "grow":
        return allPosts.filter((post) => post.collection === "grow")
      case "tools":
        return allPosts.filter((post) => post.collection === "tools")
      case "all":
        return allPosts
      default:
        return []
    }
  }, [activeTab, allPosts])

  const POSTS_TO_SHOW = 5
  const hasMorePosts = filteredPosts.length > POSTS_TO_SHOW

  return (
    <Tabs defaultValue="updates" value={activeTab} onValueChange={setActiveTab}>
      <div className="flex justify-center">
        <TabsList className="mb-6">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="updates">Updates</TabsTrigger>
          <TabsTrigger value="grow">Growth Guide</TabsTrigger>
          <TabsTrigger value="tools">Tools</TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value={activeTab} className="mt-0">
        <div className="grid grid-cols-1 gap-4">
          {filteredPosts.map((post, index) => (
            <div key={post.slug} className={cn(index >= POSTS_TO_SHOW && !isExpanded && "hidden")}>
              <BlogPost post={post} />
            </div>
          ))}
        </div>
        {hasMorePosts && (
          <div className="mt-6 flex justify-center">
            {isExpanded ? (
              <Button variant="secondary" onClick={() => setIsExpanded(false)}>
                Show Less
                <ChevronUpIcon />
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => setIsExpanded(true)}>
                Show More
                <ChevronDownIcon />
              </Button>
            )}
          </div>
        )}
      </TabsContent>
    </Tabs>
  )
}

function BlogPost({ post }: { post: Page }) {
  const locale = useLocale()
  const formattedDate = format(post.datePublished, "PP", {
    locale: config.i18n.dateFnsLocale[locale],
  })

  return (
    <Link
      href={post.slug}
      className="group relative flex h-[150px] items-center overflow-hidden rounded-2xl border bg-background bg-linear-to-bl from-foreground/[0.04] via-background to-background transition-all hover:bg-secondary dark:via-foreground/[0.04]"
    >
      {/* Image */}
      <div className="relative m-1 w-[130px] shrink-0 self-stretch overflow-hidden rounded-xl bg-secondary transition-all group-hover:scale-[1.06] md:w-[250px]">
        {post.image ? (
          <Image
            src={post.image}
            alt={post.title}
            width={500}
            height={300}
            quality={75}
            className="h-full w-full object-cover object-center"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Logo noText noLink className="opacity-20 saturate-0" size={64} />
          </div>
        )}
      </div>

      <div className="flex grow flex-col gap-0.5 py-3.5 pr-4 pl-6">
        {/* Author and Date */}
        <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 whitespace-nowrap text-muted-foreground text-sm">
          <div className="flex items-center gap-1.5">
            <Image
              src="/article/author/mike_schneider.jpg"
              alt="Mike Schneider"
              width={22}
              height={22}
              className="rounded-full"
            />
            <span>
              Mike<span className="max-sm:hidden"> Schneider</span>
            </span>
          </div>
          <span>&middot;</span>
          <span>{formattedDate}</span>
        </div>

        {/* Title */}
        <h3 className="line-clamp-1 font-bold font-display text-lg tracking-tight">{post.title}</h3>

        {/* Subtitle */}
        {post.subtitle && (
          <p className="line-clamp-2 text-muted-foreground text-sm">{post.subtitle}</p>
        )}
      </div>

      {/* Arrow Icon */}
      <div
        className={cn(
          buttonVariants({ variant: "default", size: "icon-sm" }),
          "mx-5 flex shrink-0 items-center justify-center rounded-full! border shadow-lg max-sm:hidden",
        )}
      >
        <ArrowRightIcon size={14} className="shrink-0" />
      </div>
    </Link>
  )
}
